import React, { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/css';
import * as yup from 'yup';
import { FormikProps, withFormik } from 'formik';
import ApplicationContainer from '../../../../shared/state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer, { MultipleTemplateSyncsStatus } from '../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import TemplateFormSectionModel from '../../../../shared/models/templating/api/TemplateFormSectionModel';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormFieldValueModel from '../../../../shared/models/templating/api/TemplateFormFieldValueModel';
import TemplateConfigurationModel from '../../../../shared/models/templating/api/TemplateConfigurationModel';
import ApplicationEnvironmentsContainer from '../../../../shared/state/containers/ApplicationEnvironmentsContainer';
import PanelHeaderContainer from '../../../../shared/components/structure/Panels/PanelHeaderContainer';
import PanelContainer from '../../../../shared/components/structure/Panels/PanelContainer';
import CustomScrollbars from '../../../../shared/components/structure/CustomScrollbars';
import Button from '../../../../shared/components/general/Button';
import TemplateReviewSection from '../../../../shared/scenes/applicationAppliedTemplateManager/components/TemplateReviewSection';
import Loader from '../../../../shared/components/general/Loader';
import { color_colors_growth, color_colors_growth_background, color_colors_ocean, color_text_default, color_text_link } from '../../../../shared/constants/colors';
import UpdateAppliedApplicationTemplateFormsRequest from '../../../../shared/models/templating/api/UpdateAppliedApplicationTemplateFormsRequest';
import IResult from '../../../../shared/models/result/IResult';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationType } from './ApplicationManagerContent';
import PageError from '../../../../shared/components/general/PageError';
import TelephonyDeploymentContainer from '../../../../shared/hooks/TelephonyDeploymentContainer';
import SparkDeploymentContainer from '../../../../shared/hooks/SparkDeploymentContainer';
import TemplateSaveChangesModal from '../../../../shared/components/templating/TemplateSaveChangesModal';
import SecondaryLoader from '../../../../shared/components/general/SecondaryLoader';
import styled from '@emotion/styled';

const completeIcon = require('../../../../shared/content/images/templates-icons/complete-thin.svg');
const incompleteIcon = require('../../../../shared/content/images/templates-icons/incomplete.svg');
const assistantIcon = require('../../../../shared/content/images/assistant-blue.svg');
const errorIcon = require('../../../../shared/content/images/alert-circle.svg');

interface AppliedAppTemplatesManagerProps {
    applicationId: string
    appContainer: ApplicationContainer
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    incompleteSections: TemplateFormSectionModel[]
    history: any
    appliedAppTemplateId?: string
    appliedTemplates?: AppliedApplicationTemplateModel[]
    templateConfiguration?: TemplateConfigurationModel
    applicationEnvironmentsContainer: ApplicationEnvironmentsContainer
    afterSubmit: (appliedTemplateSyncId?: string) => void
    applicationType: ApplicationType
    phoneNumberDeploymentErrorText: string
    clearTelephonyDeploymentErrors: () => void
    loadTelephonyDeployments: () => void
    isLoadingTelephonyDeployments: boolean
    calculateIncompleteSections: () => void
    fieldValidationErrors?: Record<string, boolean>
    onSectionSelect?: (section: TemplateFormSectionModel, appliedTemplateId: string) => void
}

interface AppliedAppTemplatesManagerFormData {
    templateFormValues?: TemplateFormFieldValueModel[]
    appliedTemplates?: AppliedApplicationTemplateModel[]
    isSuccess: boolean
    modifiedFields?: TemplateFormFieldValueModel[]
}

const InnerForm: React.FC<AppliedAppTemplatesManagerProps & FormikProps<AppliedAppTemplatesManagerFormData>> = (props) => {
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [submissionState, setSubmissionState] = useState<MultipleTemplateSyncsStatus>();
    const [unfilledSections, setUnfilledSections] = useState<TemplateFormSectionModel[]>([]);
    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);

    const telephonyDeploymentContainer = TelephonyDeploymentContainer.useContainer();
    const sparkDeploymentContainer = SparkDeploymentContainer.useContainer();
    const hasValidationErrors = props.fieldValidationErrors ? Object.values(props.fieldValidationErrors).some(hasError => hasError) : false;

    // Interface for sections with validation errors including template info
    interface SectionWithTemplate {
        section: TemplateFormSectionModel;
        appliedTemplateId: string;
        displayName: string;
    }

    // Get sections with validation errors for user-friendly error messages
    const getSectionsWithValidationErrors = (): SectionWithTemplate[] => {
        if (!props.fieldValidationErrors || !props.appliedTemplates) {
            return [];
        }

        const sectionsWithErrors: SectionWithTemplate[] = [];

        for (const template of props.appliedTemplates) {
            const sections = template.templateForm?.templateFormSections || [];

            for (const section of sections) {
                const sectionFieldIds = section.templateFormFields?.map(f => f.id || f.name) || [];
                const hasErrorsInSection = sectionFieldIds.some(fieldId => props.fieldValidationErrors[fieldId] === true);

                if (hasErrorsInSection) {
                    // For "Details" sections, use the template name instead
                    let displayName: string;
                    if (section.title === "Details" || section.name === "Details") {
                        displayName = template.name || section.title || section.name;
                    } else {
                        displayName = section.title || section.name || '';
                    }

                    if (displayName) {
                        sectionsWithErrors.push({
                            section,
                            appliedTemplateId: template.id,
                            displayName
                        });
                    }
                }
            }
        }

        return sectionsWithErrors;
    };

    const sectionsWithValidationErrors = getSectionsWithValidationErrors();

    // Render clickable section links for validation errors
    const renderValidationErrorLinks = (sections: SectionWithTemplate[], prefix: string) => {
        return (
            <>
                {prefix}{' '}
                {sections.map((item, index) => (
                    <React.Fragment key={`${item.appliedTemplateId}-${item.section.id || item.section.name || index}`}>
                        {props.onSectionSelect ? (
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onSectionSelect(item.section, item.appliedTemplateId);
                                }}
                                className="section-link"
                            >
                                {item.displayName}
                            </a>
                        ) : (
                            <span>{item.displayName}</span>
                        )}
                        {index < sections.length - 1 && ', '}
                    </React.Fragment>
                ))}
            </>
        );
    };

    // Render clickable section links for incomplete sections
    const renderIncompleteSectionLinks = (sections: TemplateFormSectionModel[], prefix: string) => {
        return (
            <>
                {prefix}{' '}
                {sections.map((section, index) => {
                    // For "Details" sections, use the applied template name
                    let displayName: string;
                    if (section.title === "Details" || section.name === "Details") {
                        displayName = (section as any).appliedTemplateName || section.title || section.name;
                    } else {
                        displayName = section.title || section.name || '';
                    }

                    const appliedTemplateId = (section as any).appliedTemplateId;

                    return (
                        <React.Fragment key={section.id || section.name || index}>
                            {props.onSectionSelect && appliedTemplateId ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        props.onSectionSelect(section, appliedTemplateId);
                                    }}
                                    className="section-link"
                                >
                                    {displayName}
                                </a>
                            ) : (
                                <span>{displayName}</span>
                            )}
                            {index < sections.length - 1 && ', '}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    useEffect(() => {
        telephonyDeploymentContainer.getTelephonyDeployments(props.applicationId);
    }, [])

    // Update unfilledSections whenever incompleteSections changes
    // Phone Number and Auto-Generate sections are optional and shouldn't block finishing
    useEffect(() => {
        // Filter out Phone Number sections as they are optional
        const copyIncompleteSections = [...props.incompleteSections];
        setUnfilledSections(copyIncompleteSections.filter(section =>
            section?.title !== "Phone Number" && section?.name !== "Phone Number"
        ));
    }, [props.incompleteSections])

    useEffect(() => {
        const stateOfSubmission = getSubmissionState();
        setSubmissionState(stateOfSubmission);

        // Close the modal once the submission state changes from "none" to any other state
        if (showSaveChangesModal && submissionState === "none" && stateOfSubmission !== "none") {
            setShowSaveChangesModal(false);
        }
    }, [isFormSubmitted, props.appliedAppTemplateContainer.state.multipleTemplateSyncsStatus, props.appliedAppTemplateContainer.state.isWaitingForTemplateSync, showSaveChangesModal, submissionState])

    useEffect(() => {
        props.applicationEnvironmentsContainer.loadEnvironments(props.applicationId);
    }, [props.applicationId])

    useEffect(() => {
        props.setFieldValue("appliedTemplates", props.appliedTemplates);
        // Don't reset isFormSubmitted - it causes the state to revert from "success"/"error" back to "none"
    }, [props.appliedTemplates]);


    const getSubmissionState = () => {
        if (props.templateConfiguration?.isDisabled) {
            return "templateConfigDisabled";
        }
        if (isFormSubmitted) {
            // Check sync status from container state - same pattern as full Voicify
            if (props.appliedAppTemplateContainer.state.isWaitingForTemplateSync) {
                return "syncing";
            }
            if (props.appliedAppTemplateContainer.state.multipleTemplateSyncsStatus === "syncing" as MultipleTemplateSyncsStatus) {
                return "syncing";
            }
            if (props.appliedAppTemplateContainer.state.multipleTemplateSyncsStatus === "success" as MultipleTemplateSyncsStatus) {
                return "success";
            }
            if (props.appliedAppTemplateContainer.state.multipleTemplateSyncsStatus === "error" as MultipleTemplateSyncsStatus) {
                return "error";
            }
            if (props.appliedAppTemplateContainer.state.templateSyncErrors && props.appliedAppTemplateContainer.state.templateSyncErrors.length > 0) {
                return "error";
            }
        }
        return "none";
    };

    const handleFormSubmit = () => {
        // For Spark version, we work with appliedTemplates
        if (props.values.appliedTemplates) {
            props.values.appliedTemplates.forEach(template => {
                if (template.templateFormFieldValues) {
                    template.templateFormFieldValues.forEach((templateFormValue, index) => {
                        if (templateFormValue.value === "") {
                            if (templateFormValue.defaultValue === "" || templateFormValue.defaultValue === null || templateFormValue.defaultValue === undefined) {
                                templateFormValue.value = null;
                            } else {
                                templateFormValue.value = templateFormValue.defaultValue;
                            }
                        }
                    });
                }
            });
        }
        props.handleSubmit();
    };

    const handleFinishAction = () => {
        // For the review form, always show the modal since this is the final confirmation
        setShowSaveChangesModal(true);
    };

    // Memoize the current template form values to prevent recalculation on every render
    const currentTemplateFormValues = useMemo(() => {
        const currentValues: TemplateFormFieldValueModel[] = [];
        if (props.values.appliedTemplates) {
            props.values.appliedTemplates.forEach((template) => {
                if (template.templateFormFieldValues) {
                    currentValues.push(...template.templateFormFieldValues);
                }
            });
        }
        return currentValues;
    }, [props.values.appliedTemplates]);

    const performFinish = async (modifiedFieldsOnly?: TemplateFormFieldValueModel[]) => {
        // Store modified fields in form values for use in submission
        if (modifiedFieldsOnly && modifiedFieldsOnly.length > 0) {
            await props.setFieldValue('modifiedFields', modifiedFieldsOnly);
        }

        setIsFormSubmitted(true);
        await props.submitForm();
        const stateOfSubmission = getSubmissionState();
        setSubmissionState(stateOfSubmission);
    }; const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    const submitForm = () => {
        handleFinishAction();
    };

    const goBack = () => {
        props.history.goBack();
    };

    const renderButton = () => {
        switch (submissionState) {
            case "none":
                return (
                    <Button
                        disabled={hasValidationErrors || unfilledSections.length > 0}
                        themes={["primary-small"]}
                        loading={props.appliedAppTemplateContainer.state.isSaving}
                        text={"Finish"}
                        type="button"
                        onClick={submitForm}
                    />
                );
            case "success":
            case "syncing":
                return (
                    <Button
                        disabled={unfilledSections.length > 0}
                        themes={["primary-small"]}
                        text={"Home"}
                        type="button"
                        onClick={goBack} />
                );
            case "error":
                return (
                    <Button
                        disabled={unfilledSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                        loading={props.appliedAppTemplateContainer.state.isSaving}
                        themes={["primary-small"]}
                        text={"Retry"}
                        type="button"
                        onClick={submitForm} />
                );
        }
    };

    const renderSubmissionState = () => {
        switch (submissionState) {
            case "none":
                let descriptionContent: React.ReactNode;
                if (hasValidationErrors) {
                    descriptionContent = sectionsWithValidationErrors.length > 0
                        ? renderValidationErrorLinks(sectionsWithValidationErrors, "We can't move forward yet—please go back and fix the highlighted issues in:")
                        : "One or more fields aren't in the right format.";
                } else if (unfilledSections.length > 0) {
                    descriptionContent = renderIncompleteSectionLinks(unfilledSections, "You have incomplete sections:");
                } else {
                    descriptionContent = "It looks like you've filled in all the required fields."
                }
                return (
                    <>
                        <TemplateReviewSection title="Status Check" icon={unfilledSections.length > 0 || hasValidationErrors ? incompleteIcon : completeIcon} descriptionContent={descriptionContent} />
                        <TemplateReviewSection
                            title="Generate Assistant"
                            icon={assistantIcon}
                            description={
                                hasValidationErrors ?
                                    "Check the sections listed above and correct any fields that aren't in the right format."
                                    : unfilledSections.length > 0 ?
                                        "You need to complete all sections before you can finish."
                                        : "Click \"Finish\" to generate your assistant."
                            }
                        />
                        <div className="button-container">
                            <Button
                                disabled={hasValidationErrors || unfilledSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                loading={props.appliedAppTemplateContainer.state.isSaving}
                                themes={["primary"]}
                                text={"Finish"}
                                type="button"
                                onClick={submitForm} />
                        </div>
                    </>
                );
            case "error":
                return (
                    <>
                        <TemplateReviewSection
                            title="Errors"
                            icon={errorIcon}
                            description={props.appliedAppTemplateContainer.state.templateSyncErrors.length > 0 ? props.appliedAppTemplateContainer.state.templateSyncErrors[0] : `There was a problem submitting your form.\n Try again in a few minutes.`}
                        />
                        <div className="button-container">
                            <Button disabled={unfilledSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving} loading={props.appliedAppTemplateContainer.state.isSaving}
                                themes={["primary"]}
                                text={"Retry"}
                                type="button"
                                onClick={submitForm} />
                        </div>
                    </>
                );
            case "syncing":
                return (
                    <>
                        <TemplateReviewSection
                            title="Updating Your Assistant..."
                            loader={<CustomLoader />}
                            description={`Your assistant is updating and should be ready in about 5 minutes.\nOnce it’s ready, you can update your form responses anytime if you need to make changes.`}
                        />
                        <div className="button-container">
                            <Button
                                disabled={unfilledSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                loading={props.appliedAppTemplateContainer.state.isSaving}
                                themes={["primary"]}
                                text={"Home"}
                                type="button"
                                onClick={goBack} />
                        </div>
                    </>
                );
            case "success":
                return (
                    <>
                        <TemplateReviewSection
                            title="All Done!"
                            icon={assistantIcon}
                            description={`If you need to make changes, you can update your form responses at any time.`}
                        />
                        <div className="button-container">
                            <Button
                                disabled={unfilledSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                loading={props.appliedAppTemplateContainer.state.isSaving}
                                themes={["primary"]}
                                text={"Home"}
                                type="button"
                                onClick={goBack} />
                        </div>
                    </>
                );
        }
    };

    return (
        <>
            {props.appliedAppTemplateContainer.state.isLoading || props.isLoadingTelephonyDeployments || telephonyDeploymentContainer.isLoading ?
                <Loader />
                :
                <form onSubmit={handleFormSubmit}>
                    {props.phoneNumberDeploymentErrorText &&
                        <PageError
                            errors={[`${props.phoneNumberDeploymentErrorText}`]}
                            onClear={() => props.clearTelephonyDeploymentErrors()}
                        />
                    }
                    <PanelHeaderContainer>
                        <div className={buttonContainerStyle}>
                            {renderButton()}
                        </div>
                    </PanelHeaderContainer>
                    <PanelContainer>
                        <CustomScrollbars>
                            <div className={containerStyle}>
                                <h4>Review</h4>
                                {renderSubmissionState()}
                            </div>
                        </CustomScrollbars>
                    </PanelContainer>
                </form >
            }
            {(() => {
                // Modal is loading when: 1) container is saving, 2) form is submitted but state hasn't changed from "none" yet
                const isModalLoading = props.appliedAppTemplateContainer.state.isSaving || (isFormSubmitted && submissionState === "none");
                // Build a map of current values per template for change detection
                const currentValuesMap = new Map<string, TemplateFormFieldValueModel[]>();
                if (props.values.appliedTemplates) {
                    props.values.appliedTemplates.forEach(template => {
                        if (template.templateFormFieldValues) {
                            currentValuesMap.set(template.id, template.templateFormFieldValues);
                        }
                    });
                }
                const changedFields = props.appliedAppTemplateContainer.getAllChangedFieldsForDisplay(currentValuesMap);
                return (
                    <TemplateSaveChangesModal
                        showSaveChangesModal={showSaveChangesModal}
                        closeModal={closeSaveChangesModal}
                        changedFields={changedFields}
                        submitChanges={performFinish}
                        isLoading={isModalLoading}
                        hasErrors={false}
                        isFinishAction={true}
                    />
                );
            })()}
        </>
    )
}

const CustomLoader = styled(SecondaryLoader)`
    .spinner > div {
        background: ${color_colors_ocean};
    }
`;

const buttonContainerStyle = css`
    margin-right: 16px;
    margin-left: auto;
    button {
        margin: 0 16px 0 0;
    }
`;

const containerStyle = css`
    padding: 32px;
    h4 {
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
        color: ${color_text_default};
    }
    .button-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .successful-sync {
        margin-top: 16px;
        padding: 16px;
        display: flex;
        background-color: ${color_colors_growth_background};
        border: solid 1px ${color_colors_growth};
        border-radius: 8px;
        img {
            margin-right: 16px;
        }
    }
    .section-link {
        color: ${color_text_link};
        text-decoration: none;
        cursor: pointer;
        font-weight: 600;
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    }
`;

const AppliedAppTemplateReviewForm = withFormik<AppliedAppTemplatesManagerProps, AppliedAppTemplatesManagerFormData>({
    mapPropsToValues: props => (
        {
            appliedTemplates: [...props.appliedTemplates],
            isSuccess: false,
        }
    ),
    validationSchema: yup.object().shape({
    }),
    handleSubmit: async (values, { props }) => {
        const environmentId = props.applicationEnvironmentsContainer?.findPrimaryEnvironment(props.applicationId)?.id;

        try {
            const syncFormRequests: UpdateAppliedApplicationTemplateFormsRequest[] = [];
            const concurrentSyncKey = uuidv4();
            const createSyncFormRequest = (appliedTemplate: AppliedApplicationTemplateModel) => {
                const locationSyncFormRequest: UpdateAppliedApplicationTemplateFormsRequest = {
                    appliedTemplateId: appliedTemplate.id,
                    templateFormFieldValues: appliedTemplate.templateFormFieldValues,
                    name: appliedTemplate.name,
                    publishToEnvironmentId: environmentId,
                    concurrentSyncKey: concurrentSyncKey
                };

                const commonSyncFormRequest: UpdateAppliedApplicationTemplateFormsRequest = {
                    appliedTemplateId: appliedTemplate.id,
                    templateFormFieldValues: appliedTemplate.templateFormFieldValues,
                    name: appliedTemplate.name,
                    concurrentSyncKey: concurrentSyncKey
                };

                if (props.applicationType === "location") {
                    return locationSyncFormRequest;
                } else {
                    return commonSyncFormRequest;
                }
            };
            values.appliedTemplates.forEach((appliedTemplate) => {
                syncFormRequests.push(createSyncFormRequest(appliedTemplate));
            });

            if (syncFormRequests?.length && syncFormRequests.length > 1) {
                // if there are multiple syncs the publish to environment id should only go on the first one. 
                for (let i = 1; i < syncFormRequests.length; i++) {
                    syncFormRequests[i].publishToEnvironmentId = null;
                }
            }

            // Build the modified fields per template map if modified fields are provided
            const modifiedFields = (values as any).modifiedFields as TemplateFormFieldValueModel[] | undefined;
            let modifiedFieldsPerTemplate: { [appliedTemplateId: string]: TemplateFormFieldValueModel[] } | undefined;

            if (modifiedFields && modifiedFields.length > 0) {
                modifiedFieldsPerTemplate = {};
                // Group modified fields by their appliedApplicationTemplateId
                modifiedFields.forEach(field => {
                    const templateId = field.appliedApplicationTemplateId;
                    if (templateId) {
                        if (!modifiedFieldsPerTemplate[templateId]) {
                            modifiedFieldsPerTemplate[templateId] = [];
                        }
                        modifiedFieldsPerTemplate[templateId].push(field);
                    }
                });
            }

            // Use the new unified API - process all templates in parallel (important for concurrent syncs)
            const promises = syncFormRequests.map(async (syncRequest) => {
                const modifiedFieldsForTemplate = modifiedFieldsPerTemplate?.[syncRequest.appliedTemplateId];

                return await props.appliedAppTemplateContainer.saveTemplateFields(
                    props.applicationId,
                    syncRequest.appliedTemplateId,
                    syncRequest.templateFormFieldValues,
                    {
                        finalize: true,
                        publishToEnvironmentId: syncRequest.publishToEnvironmentId,
                        concurrentSyncKey: syncRequest.concurrentSyncKey,
                        useModifiedFieldsOnly: modifiedFieldsForTemplate && modifiedFieldsForTemplate.length > 0
                    }
                );
            });

            const finalizeResults = await Promise.all(promises);
            const allSuccessful = finalizeResults.every(result => result.resultType === "Ok");

            if (!allSuccessful) {
                const syncInProgressError = "There are in process syncs for this applied template already. Please try again once existing syncs are complete."
                if (props.appliedAppTemplateContainer.state.templateSyncErrors.includes(syncInProgressError)) {
                    props.appliedAppTemplateContainer.setState({ ...props.appliedAppTemplateContainer.state, templateSyncErrors: ["Looks like there's another assistant update underway. Please wait for it to finish and try again in a few minutes."] });
                }
            }
            else {
                // All syncs successful - get sync ID from container state and pass to afterSubmit for polling
                const syncId = props.appliedAppTemplateContainer.state.appliedTemplateSyncId;
                props.afterSubmit(syncId);
            }

        } catch (error) { }
    }
})(InnerForm);


export default AppliedAppTemplateReviewForm;